import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { getSaveaPhone, getSaveaPhoneTel, getSpecialistMessage } from '../config/phoneConfig';
import { 
  Sun, 
  Home,
  DollarSign, 
  TrendingUp,
  MapPin,
  Calculator,
  Leaf,
  Zap,
  Calendar,
  Target,
  Award,
  AlertCircle,
  Info,
  Phone,
  ExternalLink
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

export const SolarSimulatorPage = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    city: '',
    roofArea: [50],
    monthlyBill: [120],
    orientation: 'south',
    inclination: [30],
    system_efficiency: [85]
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const cities = {
    FR: [
      { name: 'Paris', irradiation: 1050 },
      { name: 'Marseille', irradiation: 1450 },
      { name: 'Lyon', irradiation: 1200 },
      { name: 'Nice', irradiation: 1380 },
      { name: 'Toulouse', irradiation: 1250 },
      { name: 'Bordeaux', irradiation: 1180 }
    ],
    ES: [
      { name: 'Madrid', irradiation: 1550 },
      { name: 'Barcelona', irradiation: 1400 },
      { name: 'Valencia', irradiation: 1650 },
      { name: 'Sevilla', irradiation: 1750 },
      { name: 'Málaga', irradiation: 1680 },
      { name: 'Las Palmas', irradiation: 1950 }
    ]
  };

  const orientations = [
    { value: 'south', label: i18n.language === 'es' ? 'Sur (óptimo)' : 'Sud (optimal)', multiplier: 1.0 },
    { value: 'southeast', label: i18n.language === 'es' ? 'Sudeste' : 'Sud-Est', multiplier: 0.95 },
    { value: 'southwest', label: i18n.language === 'es' ? 'Sudoeste' : 'Sud-Ouest', multiplier: 0.95 },
    { value: 'east', label: i18n.language === 'es' ? 'Este' : 'Est', multiplier: 0.85 },
    { value: 'west', label: i18n.language === 'es' ? 'Oeste' : 'Ouest', multiplier: 0.85 }
  ];

  const calculateSolarProduction = () => {
    setLoading(true);
    
    setTimeout(() => {
      const selectedCity = cities[i18n.language === 'es' ? 'ES' : 'FR'].find(city => 
        city.name === formData.city
      );
      
      if (!selectedCity) {
        toast.error(i18n.language === 'es' ? 'Selecciona una ciudad' : 'Sélectionnez une ville');
        setLoading(false);
        return;
      }

      const roofArea = formData.roofArea[0];
      const monthlyBill = formData.monthlyBill[0];
      const inclination = formData.inclination[0];
      const efficiency = formData.system_efficiency[0] / 100;
      
      const orientationMultiplier = orientations.find(o => o.value === formData.orientation)?.multiplier || 1;
      const inclinationMultiplier = 1 - (Math.abs(inclination - 35) / 100);
      
      const systemSizeKwp = (roofArea * 6.5) / 100;
      const annualIrradiation = selectedCity.irradiation * orientationMultiplier * inclinationMultiplier;
      const annualProductionKwh = systemSizeKwp * annualIrradiation * efficiency;
      
      const electricityPrice = i18n.language === 'es' ? 0.25 : 0.18;
      const currentAnnualConsumption = (monthlyBill / electricityPrice) * 12;
      const selfConsumptionRate = Math.min(annualProductionKwh / currentAnnualConsumption, 0.7);
      const selfConsumedKwh = annualProductionKwh * selfConsumptionRate;
      const surplusKwh = annualProductionKwh - selfConsumedKwh;
      const sellBackPrice = electricityPrice * 0.6;
      
      const annualSavings = selfConsumedKwh * electricityPrice + surplusKwh * sellBackPrice;
      const installationCost = systemSizeKwp * (i18n.language === 'es' ? 1800 : 1600);
      const paybackPeriod = installationCost / annualSavings;
      const twentyYearSavings = (annualSavings * 20) - installationCost;
      
      const co2PerKwh = 0.23;
      const annualCo2Savings = annualProductionKwh * co2PerKwh;

      const calculatedResults = {
        city: selectedCity,
        systemSize: systemSizeKwp,
        roofArea,
        annualProduction: annualProductionKwh,
        annualSavings,
        installationCost,
        paybackPeriod,
        twentyYearSavings,
        monthlyProduction: annualProductionKwh / 12,
        selfConsumption: selfConsumptionRate * 100,
        surplus: surplusKwh,
        co2Savings: annualCo2Savings,
        monthlyData: Array.from({length: 12}, (_, i) => ({
          month: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'][i],
          production: Math.round(annualProductionKwh / 12 * (1 + 0.3 * Math.sin((i - 5) * Math.PI / 6))),
          savings: Math.round(annualSavings / 12 * (1 + 0.3 * Math.sin((i - 5) * Math.PI / 6)))
        }))
      };
      
      setResults(calculatedResults);
      setLoading(false);
      toast.success(i18n.language === 'es' ? '¡Cálculo completado!' : 'Calcul terminé !');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      {/* Hero Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4">
              {t('tools.solar.title')}
            </h1>
            <p className="text-xl text-sage-600 max-w-3xl mx-auto">
              {i18n.language === 'es'
                ? 'Calcula el potencial de ahorro de una instalación solar en tu hogar. Simulador basado en datos reales de irradiación.'
                : 'Calculez le potentiel d\'économies d\'une installation solaire pour votre foyer. Simulateur basé sur données réelles d\'irradiation.'
              }
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Configuration Form avec sliders bien visibles */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-orange-600" />
                  {i18n.language === 'es' ? 'Configuración del Sistema' : 'Configuration du Système'}
                </CardTitle>
                <CardDescription>
                  {i18n.language === 'es'
                    ? 'Ingresa los datos de tu vivienda para obtener una estimación precisa'
                    : 'Renseignez les données de votre logement pour obtenir une estimation précise'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* City Selection */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4" />
                    {i18n.language === 'es' ? 'Ciudad' : 'Ville'}
                  </Label>
                  <select
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full border-2 border-sage-200 rounded-lg p-3 focus:border-sage-500 bg-white"
                  >
                    <option value="">
                      {i18n.language === 'es' ? 'Selecciona tu ciudad...' : 'Sélectionnez votre ville...'}
                    </option>
                    {cities[i18n.language === 'es' ? 'ES' : 'FR'].map(city => (
                      <option key={city.name} value={city.name}>
                        {city.name} ({city.irradiation} kWh/m²/an)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Roof Area - Slider bien visible */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Home className="w-4 h-4" />
                    {i18n.language === 'es' ? `Superficie del techo: ${formData.roofArea[0]} m²` : `Surface de toiture : ${formData.roofArea[0]} m²`}
                  </Label>
                  <div className="px-3">
                    <Slider
                      value={formData.roofArea}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, roofArea: value }))}
                      max={200}
                      min={20}
                      step={5}
                      className="w-full slider-visible"
                    />
                    <div className="flex justify-between text-xs text-sage-500 mt-2">
                      <span>20 m²</span>
                      <span>200 m²</span>
                    </div>
                  </div>
                </div>

                {/* Monthly Bill - Slider bien visible */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4" />
                    {i18n.language === 'es' ? `Factura mensual actual: ${formData.monthlyBill[0]}€` : `Facture mensuelle actuelle : ${formData.monthlyBill[0]}€`}
                  </Label>
                  <div className="px-3">
                    <Slider
                      value={formData.monthlyBill}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, monthlyBill: value }))}
                      max={300}
                      min={30}
                      step={10}
                      className="w-full slider-visible"
                    />
                    <div className="flex justify-between text-xs text-sage-500 mt-2">
                      <span>30€</span>
                      <span>300€</span>
                    </div>
                  </div>
                </div>

                {/* Orientation */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Sun className="w-4 h-4" />
                    {i18n.language === 'es' ? 'Orientación del techo' : 'Orientation du toit'}
                  </Label>
                  <select
                    value={formData.orientation}
                    onChange={(e) => setFormData(prev => ({ ...prev, orientation: e.target.value }))}
                    className="w-full border-2 border-sage-200 rounded-lg p-3 focus:border-sage-500 bg-white"
                  >
                    {orientations.map(orientation => (
                      <option key={orientation.value} value={orientation.value}>
                        {orientation.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Inclination - Slider bien visible */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4" />
                    {i18n.language === 'es' ? `Inclinación: ${formData.inclination[0]}°` : `Inclinaison : ${formData.inclination[0]}°`}
                  </Label>
                  <div className="px-3">
                    <Slider
                      value={formData.inclination}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, inclination: value }))}
                      max={90}
                      min={0}
                      step={5}
                      className="w-full slider-visible"
                    />
                    <div className="flex justify-between text-xs text-sage-500 mt-2">
                      <span>0° (plat)</span>
                      <span>90° (vertical)</span>
                    </div>
                  </div>
                </div>

                {/* System Efficiency - Slider bien visible */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Award className="w-4 h-4" />
                    {i18n.language === 'es' ? `Eficiencia del sistema: ${formData.system_efficiency[0]}%` : `Efficacité du système : ${formData.system_efficiency[0]}%`}
                  </Label>
                  <div className="px-3">
                    <Slider
                      value={formData.system_efficiency}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, system_efficiency: value }))}
                      max={95}
                      min={70}
                      step={1}
                      className="w-full slider-visible"
                    />
                    <div className="flex justify-between text-xs text-sage-500 mt-2">
                      <span>70% (basique)</span>
                      <span>95% (premium)</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    onClick={calculateSolarProduction}
                    disabled={!formData.city || loading}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold"
                  >
                    {loading ? (
                      <div className="loading-spinner mr-2" />
                    ) : (
                      <Calculator className="w-4 h-4 mr-2" />
                    )}
                    {loading 
                      ? (i18n.language === 'es' ? 'Calculando...' : 'Calcul en cours...') 
                      : (i18n.language === 'es' ? 'Calcular Ahorros' : 'Calculer Économies')
                    }
                  </Button>
                  
                  <Button 
                    onClick={() => setFormData({ city: '', roofArea: [50], monthlyBill: [120], orientation: 'south', inclination: [30], system_efficiency: [85] })}
                    variant="outline"
                    className="border-sage-200 text-sage-600 hover:bg-sage-50"
                  >
                    {i18n.language === 'es' ? 'Reiniciar' : 'Reset'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            {results ? (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sun className="w-5 h-5 text-orange-600" />
                      {i18n.language === 'es' ? 'Resultados de la Simulación' : 'Résultats de Simulation'}
                    </CardTitle>
                    <CardDescription>
                      {i18n.language === 'es' ? 'Para' : 'Pour'} {results.city.name} • {results.systemSize.toFixed(1)} kWp • {results.roofArea} m²
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-700">
                          {results.annualSavings.toFixed(0)}€
                        </div>
                        <div className="text-sm text-green-600">
                          {i18n.language === 'es' ? 'Ahorro anual' : 'Économie annuelle'}
                        </div>
                      </div>
                      
                      <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-2xl font-bold text-blue-700">
                          {results.paybackPeriod.toFixed(1)}
                        </div>
                        <div className="text-sm text-blue-600">
                          {i18n.language === 'es' ? 'Años amortización' : 'Années amortissement'}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-sage-600">
                          {i18n.language === 'es' ? 'Producción anual:' : 'Production annuelle :'}
                        </span>
                        <span className="font-medium">{results.annualProduction.toFixed(0)} kWh</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-sage-600">
                          {i18n.language === 'es' ? 'Coste instalación:' : 'Coût installation :'}
                        </span>
                        <span className="font-medium">{results.installationCost.toFixed(0)}€</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Monthly Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {i18n.language === 'es' ? 'Producción Mensual' : 'Production Mensuelle'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={results.monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Bar 
                          dataKey="production" 
                          fill="#f59e0b" 
                          name={i18n.language === 'es' ? 'Production (kWh)' : 'Production (kWh)'}
                        />
                        <Bar 
                          dataKey="savings" 
                          fill="#10b981" 
                          name={i18n.language === 'es' ? 'Ahorros (€)' : 'Économies (€)'}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="flex items-center justify-center p-12 text-center">
                <div>
                  <Sun className="w-16 h-16 text-orange-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-sage-700 mb-2">
                    {i18n.language === 'es' ? 'Configura tu simulación' : 'Configurez votre simulation'}
                  </h3>
                  <p className="text-sage-500 max-w-md">
                    {i18n.language === 'es'
                      ? 'Completa los parámetros para calcular tus ahorros potenciales con energía solar'
                      : 'Complétez les paramètres pour calculer vos économies potentielles avec l\'énergie solaire'
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Section Panneaux Solaires DIY */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-sage-900 mb-4">
              ☀️ {i18n.language === 'es' ? 'Paneles Solares DIY (avanzado)' : 'Panneaux solaires DIY (avancé)'}
            </h2>
          </div>

          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="font-bold text-blue-700">{i18n.language === 'es' ? 'Tiempo' : 'Temps'}</div>
                  <div className="text-sm text-blue-600">{i18n.language === 'es' ? '1 a 2 horas' : '1 à 2 heures'}</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="font-bold text-orange-700">{i18n.language === 'es' ? 'Dificultad' : 'Difficulté'}</div>
                  <div className="text-sm text-orange-600">{i18n.language === 'es' ? 'avanzada' : 'avancée'}</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="font-bold text-green-700">{i18n.language === 'es' ? 'Ahorro estimado' : 'Économie estimée'}</div>
                  <div className="text-sm text-green-600">80 {i18n.language === 'es' ? 'a' : 'à'} 120 €/an</div>
                </div>
              </div>

              {i18n.language === 'es' ? (
                // VERSION ESPAGNOLE
                <div className="space-y-4 text-sage-700">
                  <p className="leading-relaxed">
                    Los kits "plug & play" (que se conectan a un enchufe normal) vendidos en España (diversas marcas) cuestan entre 500 y 1.000 €. Se paga principalmente el marketing. Es una buena opción pero un poco caro y más largo de rentabilizar.
                  </p>
                  
                  <p className="leading-relaxed">
                    He elegido otra vía: comprar directamente a un proveedor. La ventaja en España es que hay disponibilidad inmediata y precios muy competitivos.
                  </p>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 my-6">
                    <h4 className="font-bold text-green-800 mb-2">👉 Ejemplo concreto:</h4>
                    <p className="text-green-700 text-sm">
                      250 € por un panel de 555W, simplemente colocado en mi jardín expuesto al sur. 
                      Produce 2 a 3 kWh/día en verano. Rentabilidad en 2-3 años, luego es electricidad gratis durante 20 años.
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-bold text-blue-800 mb-3">📌 Enlaces útiles:</h4>
                    <div className="space-y-2">
                      <a 
                        href="https://lumisolar.es/kit-solar-autoinstalable-555w" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Kit Solar Autoinstalable 555W - LumiSolar
                      </a>
                      <p className="text-xs text-blue-600 mt-2">
                        Proveedor directo con precios competitivos para España
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                // VERSION FRANÇAISE
                <div className="space-y-4 text-sage-700">
                  <p className="leading-relaxed">
                    Les kits "plug & play" (=qui se branchent à une prise normale) vendus en France (Beem, Sunology…) coûtent entre 500 et 1 000 €. On paie surtout le marketing. C'est une bonne option mais un peu cher et plus long à rentabiliser.
                  </p>
                  
                  <p className="leading-relaxed">
                    J'ai choisi une autre voie : acheter directement auprès d'un fournisseur. Pour l'instant la livraison n'est possible qu'en Espagne mais je suis en train de vous négocier un partenariat pour que ce soit livrable en France.
                  </p>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 my-6">
                    <h4 className="font-bold text-green-800 mb-2">👉 Exemple concret :</h4>
                    <p className="text-green-700 text-sm">
                      250 € pour un panneau de 555W, simplement posé dans mon jardin exposé plein sud. 
                      Ça produit 2 à 3 kWh/jour en été. Rentabilité en 2-3 ans, ensuite c'est de l'électricité gratuite pour 20 ans.
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-bold text-blue-800 mb-3">📌 Liens utiles :</h4>
                    <div className="space-y-2">
                      <a 
                        href="https://lumisolar.es/kit-solar-autoinstalable-555w" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Kit Solar Autoinstalable 555W - LumiSolar
                      </a>
                      <p className="text-xs text-blue-600 mt-2">
                        Fournisseur direct avec prix compétitifs (livraison Espagne pour l'instant)
                      </p>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                    <h4 className="font-bold text-yellow-800 mb-2">⚠️ En France :</h4>
                    <p className="text-yellow-700 text-sm">
                      C'est plus compliqué de trouver ce type de panneau à prix compétitif. Sur LeBonCoin, vous pouvez trouver un panneau + un micro-onduleur + un câble pour brancher directement dans une prise.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA SAVEA Simulateur Solaire */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-yellow-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {i18n.language === 'es' ? '¿Sabías que la clave del ahorro es tener la oferta energética adaptada?' : 'Saviez-vous que la clé des économies est d\'avoir l\'offre énergétique adaptée ?'}
          </h2>
          <p className="text-orange-100 text-lg mb-8">
            {i18n.language === 'es' 
              ? 'Antes de instalar paneles solares, asegúrate de tener la mejor tarifa eléctrica. Nuestros especialistas te ayudan a optimizar tu contrato actual.'
              : 'Avant d\'installer des panneaux solaires, assurez-vous d\'avoir le meilleur tarif électrique. Nos spécialistes vous aident à optimiser votre contrat actuel.'
            }
          </p>
          
          <Card className="bg-white p-8 text-center max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl flex items-center justify-center">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-bold text-sage-900 mb-2">
              {i18n.language === 'es' ? 'Especialista Energía SAVEA' : 'Spécialiste Énergie SAVEA'}
            </h3>
            <p className="text-sage-600 mb-6 text-sm">
              {i18n.language === 'es' 
                ? 'Optimiza tu contrato antes de invertir en solar'
                : 'Optimisez votre contrat avant d\'investir dans le solaire'
              }
            </p>
            <Button 
              onClick={() => window.open(`tel:${getSaveaPhoneTel(i18n.language === 'es' ? 'ES' : 'FR', 'electricity')}`)}
              size="lg"
              className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-bold"
            >
              <Phone className="w-5 h-5 mr-2" />
              {getSaveaPhone(i18n.language === 'es' ? 'ES' : 'FR', 'electricity')}
            </Button>
            <p className="text-xs text-sage-500 mt-3">
              {i18n.language === 'es' 
                ? 'Servicio gratuito - Asesoramiento energético personalizado'
                : 'Appel gratuit - Conseil énergétique personnalisé'
              }
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
};